import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Formik, Form } from 'formik';

import { SR_API_ACTION_GET_LISTING_BY_ID, SR_API_ACTION_UPSERT_LISTING } from 'api/actionTypes';

import postWithState from 'client/actions/postWithState';

import {
  LISTING_WALKTHROUGH_CONDITION_ITEMS,
  LISTING_WALKTHROUGH_CONDITION_OTHER,
  LISTING_WALKTHROUGH_CONDITION_READY,
} from 'common/listingConstants';

import {
  createFormValidationFn,
  createInitialValues,
  hideFieldIf,
  validateFormFieldsAndSubmit,
} from 'client/helpers/formikHelpers';

import SrBox from 'client/components/SrBox';
import SrModalV2, { MODAL_THEME_FIXED_FOOTER_BUTTON } from 'client/components/SrModalV2/SrModalV2';
import SrFormikField, { SR_FIELD_THEME_ADOBE } from 'client/components/SrFormikField';
import {
  SR_BUTTON_THEME_ADOBE_GREY,
  SR_BUTTON_THEME_ADOBE_GREEN,
} from 'client/components/SrButton/SrButton';

const getFormFields = ({
  formValues: { walkthroughConditions = [] },
  onWalkthroughConditionsChange,
}) => {
  const formFields = [
    hideFieldIf(true, {
      id: 'id',
    }),
    {
      id: 'walkthroughConditions',
      title: 'Ready to be listed?',
      type: 'multiple_choice',
      properties: {
        choices: LISTING_WALKTHROUGH_CONDITION_ITEMS,
        onChange: onWalkthroughConditionsChange,
        autoFitWidth: '300px',
      },
      margin: '0',
    },
    hideFieldIf(!walkthroughConditions.includes(LISTING_WALKTHROUGH_CONDITION_OTHER), {
      id: 'walkthroughExtraCondition',
      title: 'Not ready reason other ',
      type: 'long_text',
      validations: ['required'],
    }),
    {
      id: 'walkthroughNotes',
      title: 'Walkthrough notes',
      subtitle: 'Notes sent to the partner',
      type: 'long_text',
    },
    {
      id: 'walkthroughPhotoUrls',
      title: 'Walkthrough Photos',
      type: 'file_multi',
      properties: {
        filePath: '/listing-uploads/walkthrough/{uuid}',
        fileAccept: 'image/*',
      },
    },
  ];

  return formFields;
};

const SrListingEditWalkThroughFieldsModal = ({
  marketingPartnerListing,
  onClose,
  dispatch,
  reloadDataTableMarketingPartnerListing,
}) => {
  const [listing, setListing] = useState(null);

  useEffect(() => {
    dispatch(postWithState, {
      action: SR_API_ACTION_GET_LISTING_BY_ID,
      payload: { listingId: marketingPartnerListing.listingId },
      onSuccess: (response) => setListing(response),
    });
  }, [marketingPartnerListing, dispatch]);

  if (!listing) {
    return (
      <SrModalV2 buttonProps={[]}>
        <div className="loading-gif" />
      </SrModalV2>
    );
  }

  return (
    <Formik
      initialValues={createInitialValues({
        formFields: getFormFields({ formValues: listing }),
        defaultValues: listing,
      })}
      enableReinitialize
      validate={(formValues) => {
        const formFields = getFormFields({ formValues });
        return createFormValidationFn(formFields)(formValues);
      }}
      onSubmit={(formValues) => {
        dispatch(postWithState, {
          action: SR_API_ACTION_UPSERT_LISTING,
          payload: { listing: formValues },
          onSuccess: () => {
            reloadDataTableMarketingPartnerListing(marketingPartnerListing);
            onClose();
          },
        });
      }}
    >
      {(formikBag) => {
        const { values, setFieldValue } = formikBag;
        const formFields = getFormFields({
          formValues: values,
          onWalkthroughConditionsChange: (newValue) => {
            const previousWalkthroughConditions = values.walkthroughConditions;
            if (!Array.isArray(newValue) || newValue.length === 0) {
              setFieldValue('walkthroughConditions', newValue);
            } else if (
              !previousWalkthroughConditions.includes(LISTING_WALKTHROUGH_CONDITION_READY) &&
              newValue.includes(LISTING_WALKTHROUGH_CONDITION_READY)
            ) {
              setFieldValue('walkthroughConditions', [LISTING_WALKTHROUGH_CONDITION_READY]);
            } else {
              const newValueWithoutReady = newValue.filter(
                (walkthroughCondition) =>
                  walkthroughCondition !== LISTING_WALKTHROUGH_CONDITION_READY,
              );
              setFieldValue('walkthroughConditions', newValueWithoutReady);
            }
          },
        });

        return (
          <SrModalV2
            theme={MODAL_THEME_FIXED_FOOTER_BUTTON}
            buttonTheme={SR_BUTTON_THEME_ADOBE_GREY}
            contentPadding="28px 20px 120px 20px"
            buttonProps={[
              {
                text: 'Close',
                theme: SR_BUTTON_THEME_ADOBE_GREY,
                onClick: onClose,
              },
              {
                text: 'Save',
                theme: SR_BUTTON_THEME_ADOBE_GREEN,
                onClick: () =>
                  validateFormFieldsAndSubmit({
                    formikBag,
                    formFields,
                  }),
              },
            ]}
          >
            <SrBox>
              <Form className="form">
                {formFields.map((formField) => (
                  <SrBox key={formField.id}>
                    <SrFormikField
                      formField={formField}
                      formikBag={formikBag}
                      theme={SR_FIELD_THEME_ADOBE}
                      margin={formField.margin}
                    />
                  </SrBox>
                ))}
              </Form>
            </SrBox>
          </SrModalV2>
        );
      }}
    </Formik>
  );
};

SrListingEditWalkThroughFieldsModal.propTypes = {
  listingId: PropTypes.number.isRequired,
  marketingPartnerListing: PropTypes.object,
  reloadDataTableMarketingPartnerListing: PropTypes.func,
  onClose: PropTypes.func.isRequired,
  dispatch: PropTypes.func.isRequired,
};

SrListingEditWalkThroughFieldsModal.displayName = 'SrListingEditWalkThroughFieldsModal';

export default SrListingEditWalkThroughFieldsModal;
